import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@ui-nuvertos/shared';

export interface VendorFilters {
  type?: 'all' | 'draft' | 'non-draft';
  limit?: number;
  offset?: number;
  company_name?: string;
  phone?: string;
  category?: string;
  is_deleted?: boolean;
  approval_status?: string;
  created_at?: string;
  entity_type?: string;
}

export interface VendorApiResponse {
  data: {
    total: number;
    vendors: any[];
  };
}

@Injectable({
  providedIn: 'root'
})
export class VendorService {
  private readonly API_BASE_URL = environment.accountingUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get all vendors with optional filters
   * Endpoint: GET /api/v1/vendors?type=all&limit=10&offset=0
   * Query Parameters: type (all/draft/non-draft), limit, offset, company_name, category, is_deleted
   */
  getAllVendors(filters?: VendorFilters): Observable<VendorApiResponse> {
    let params = new HttpParams();

    if (filters) {
      // Add type parameter (default to 'all')
      if (filters.type) {
        params = params.set('type', filters.type);
      } else {
        params = params.set('type', 'all');
      }

      // Add pagination parameters
      if (filters.limit) {
        params = params.set('limit', filters.limit.toString());
      }
      if (filters.offset) {
        params = params.set('offset', filters.offset.toString());
      }

      // Add filter parameters
      if (filters.company_name) {
        params = params.set('company_name', filters.company_name);
      }
      if (filters.category) {
        params = params.set('category', filters.category);
      }
      if(filters.phone) {
        params = params.set('phone', filters.phone);
      }
      if(filters.approval_status) {
        params = params.set('approval_status', filters.approval_status);
      }
      if(filters.created_at) {
        params = params.set('created_at', filters.created_at);
      }
      if(filters.entity_type) {
        params = params.set('entity_type', filters.entity_type);
      }

    } else {
      params = params.set('type', 'all');
    }

    return this.http.get<VendorApiResponse>(`${this.API_BASE_URL}/api/v1/vendors/`, { 
      params,
      withCredentials: true 
    });
  }

  /**
   * Get single vendor by ID
   * Endpoint: GET /api/v1/vendors/{vendorId}
   */
  getSingleVendor(vendorId: string): Observable<any> {
    return this.http.get<any>(`${this.API_BASE_URL}/api/v1/vendors/${vendorId}`, {
      withCredentials: true 
    });
  }

  /**
   * Create a new vendor
   * Endpoint: POST /api/v1/vendors/
   */
  createVendor(vendorData: any): Observable<any> {
    return this.http.post<any>(`${this.API_BASE_URL}/vendors/`, vendorData);
  }

  /**
   * Update an existing vendor
   * Endpoint: PUT /api/v1/vendors/{vendorId}
   */
  updateVendor(vendorId: string, vendorData: any): Observable<any> {
    return this.http.put<any>(`${this.API_BASE_URL}/vendors/${vendorId}`, vendorData);
  }

  /**
   * Delete a vendor
   * Endpoint: DELETE /api/v1/vendors/{vendorId}
   */
  deleteVendor(vendorId: string): Observable<any> {
    return this.http.delete<any>(`${this.API_BASE_URL}/api/v1/vendors/${vendorId}`, {
      withCredentials: true 
    });
  }

  /**
   * Get vendor subsidiaries
   * Endpoint: GET /api/v1/vendors/{vendorId}/subsidiaries
   * Retrieves all subsidiaries for a vendor based on vendor classifications
   */
  getVendorSubsidiaries(vendorId: string): Observable<any> {
    return this.http.get<any>(`${this.API_BASE_URL}/api/v1/vendors/${vendorId}/subsidiaries`, {
      withCredentials: true 
    });
  }
}